import {
  AvExportSession,
  VIDEO_CODECS,
  VideoExportSession,
  chooseContainer,
  defaultChunkFrames,
  frameDurationMicros,
  selectAudioCodec,
  selectVideoCodec,
  suggestedAudioBitrate,
  suggestedBitrate,
  type AudioCodecSelection,
  type AvExportSettings,
  type CodecSelection,
  type ContainerChoice,
  type ContainerFormat,
  type ReRenderSession,
  type VideoEncodeConfig,
} from '../../visual/index.js';
import type { RenderingPrototype } from '../prototype/RenderingPrototype.js';
import { AudioBufferPcmSource, AudioDataChunkSource, audioDataAvailable } from './AudioBufferPcm.js';
import { CanvasFrameSource, type CaptureMethod } from './CanvasFrameSource.js';
import {
  MediabunnyMuxer,
  muxerAudioCodec,
  muxerSupports,
  muxerVideoCodec,
} from './MediabunnyMuxer.js';
import { WebCodecsAudioEncoder, audioEncoderAvailable, probeAudioConfig } from './WebCodecsAudioEncoder.js';
import { WebCodecsVideoEncoder, probeVideoConfig, webCodecsAvailable } from './WebCodecsVideoEncoder.js';

/**
 * Builds an audio-and-video export for this browser, or explains why it cannot.
 *
 * `createVideoExport` did this for Phase 7A and this is its successor rather
 * than its replacement — the video half is assembled exactly the same way, from
 * the same `CanvasFrameSource` and `WebCodecsVideoEncoder`. What is added is the
 * part that could not be decided one track at a time.
 *
 * ## Codecs cannot be chosen independently
 *
 * A video codec, an audio codec and a container form one decision. Choosing the
 * video codec first and the audio codec second produces H.264 with Opus, which
 * MP4 can technically hold and half of the world's players will not open; the
 * other order produces AAC with VP9, which WebM cannot hold at all. So the
 * search runs over combinations: for each video codec the host accepts, look for
 * an audio codec that its container can carry, and take the first pair that
 * works. Phase 7A already found a machine where a 1920×1920 export has no H.264
 * encoder and falls back to VP9 — on that path this now also moves the audio to
 * Opus and the container to WebM, rather than producing an MP4 that no muxer
 * should have been asked to write.
 */

export interface AvExportBuild {
  readonly session: AvExportSession;
  readonly source: CanvasFrameSource;
  readonly videoEncoder: WebCodecsVideoEncoder;
  readonly audioEncoder: WebCodecsAudioEncoder | null;
  readonly muxer: MediabunnyMuxer;
  readonly choice: ContainerChoice;
  readonly videoConfig: VideoEncodeConfig;
  readonly videoSelection: CodecSelection;
  readonly audioSelection: AudioCodecSelection | null;
  readonly settings: AvExportSettings;
}

export type AvExportFailure =
  | { readonly reason: 'no-webcodecs'; readonly message: string }
  | { readonly reason: 'no-audio-encoder'; readonly message: string }
  | { readonly reason: 'no-codec'; readonly message: string; readonly tried: readonly string[] }
  | { readonly reason: 'no-audio-codec'; readonly message: string; readonly tried: readonly string[] }
  | { readonly reason: 'incompatible'; readonly message: string }
  | { readonly reason: 'too-large'; readonly message: string }
  | { readonly reason: 'invalid-pcm'; readonly message: string };

export type AvExportAttempt =
  | ({ readonly ok: true } & AvExportBuild)
  | ({ readonly ok: false } & AvExportFailure);

export interface CreateAvExportOptions {
  readonly prototype: RenderingPrototype;
  readonly session: ReRenderSession;
  readonly settings: AvExportSettings;
  readonly width: number;
  readonly height: number;
  /**
   * The song's decoded audio. Omit for a video-only export.
   *
   * The **original** decoded PCM — never the analysis timeline the visuals run
   * on. See `AudioExport.ts` for why that distinction matters.
   */
  readonly audio?: AudioBuffer | null;
  readonly captureMethod?: CaptureMethod;
  readonly videoBitrate?: number;
  readonly audioBitrate?: number;
  /** Forces a container. Left unset, the codecs decide. */
  readonly container?: ContainerFormat;
  readonly now?: () => number;
  readonly yieldToHost?: () => Promise<void>;
}

export async function createAvExport(options: CreateAvExportOptions): Promise<AvExportAttempt> {
  if (!webCodecsAvailable()) {
    return {
      ok: false,
      reason: 'no-webcodecs',
      message: 'This browser has no WebCodecs VideoEncoder; export is unavailable.',
    };
  }

  const gl = options.prototype.stage.renderer.getContext();
  const maxDimension = (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) || 4096;
  if (options.width > maxDimension || options.height > maxDimension) {
    return {
      ok: false,
      reason: 'too-large',
      message:
        `${options.width}x${options.height} exceeds this GPU's limit of ${maxDimension}px; ` +
        'choose a smaller resolution.',
    };
  }

  const wantsAudio = options.audio !== undefined && options.audio !== null;
  if (wantsAudio) {
    const problem = describePcmProblem(options.audio as AudioBuffer);
    if (problem !== null) return { ok: false, reason: 'invalid-pcm', message: problem };
    if (!audioEncoderAvailable() || !audioDataAvailable()) {
      return {
        ok: false,
        reason: 'no-audio-encoder',
        message: 'This browser has no WebCodecs AudioEncoder; export audio off, or use video only.',
      };
    }
  }

  const pcm = wantsAudio ? new AudioBufferPcmSource(options.audio as AudioBuffer) : null;
  const videoBitrate =
    options.videoBitrate ?? suggestedBitrate(options.width, options.height, options.settings.fps);

  // The combination search: every video codec the host accepts, paired with an
  // audio codec its container can carry. First pair that works wins, and the
  // order of `VIDEO_CODECS` is the preference — H.264/MP4 before VP9/WebM.
  const triedVideo: { name: string; reason: string }[] = [];
  const triedAudio: { name: string; reason: string }[] = [];
  let videoSelection: CodecSelection | null = null;
  let audioSelection: AudioCodecSelection | null = null;
  let choice: ContainerChoice | null = null;

  for (const candidate of VIDEO_CODECS) {
    if (options.container !== undefined && candidate.container !== options.container) continue;
    if (!muxerSupports(candidate.container)) {
      triedVideo.push({ name: candidate.name, reason: 'the muxer cannot write that container' });
      continue;
    }
    if (muxerVideoCodec(candidate.codec) === null) {
      triedVideo.push({ name: candidate.name, reason: 'the muxer cannot describe that codec' });
      continue;
    }

    const video = await selectVideoCodec({
      probe: probeVideoConfig,
      width: options.width,
      height: options.height,
      framerate: options.settings.fps,
      bitrate: videoBitrate,
      candidates: [candidate],
    });
    if (video === null) {
      triedVideo.push({ name: candidate.name, reason: 'this browser cannot encode it at this size' });
      continue;
    }

    let audio: AudioCodecSelection | null = null;
    if (pcm !== null) {
      const found = await selectAudioCodec({
        probe: probeAudioConfig,
        sampleRate: pcm.sampleRate,
        channelCount: pcm.channelCount,
        bitrate: options.audioBitrate ?? 128_000,
        // The bitrate is asked about, not decided afterwards: support is
        // per-configuration, and probing at one rate then configuring at
        // another is how a codec comes back "supported" and is then refused.
        bitrateFor: (entry) =>
          options.audioBitrate ?? suggestedAudioBitrate(entry, pcm.channelCount),
        container: candidate.container,
      });
      if (found === null) {
        triedAudio.push({
          name: candidate.container.toUpperCase(),
          reason: 'no encodable audio codec fits this container',
        });
        triedVideo.push({ name: candidate.name, reason: 'no audio codec to go with it' });
        continue;
      }
      if (muxerAudioCodec(found.candidate.codec) === null) {
        triedAudio.push({ name: found.candidate.name, reason: 'the muxer cannot describe it' });
        continue;
      }
      audio = found;
      triedAudio.push({ name: found.candidate.name, reason: 'accepted' });
    }

    const combination = chooseContainer({
      video: video.candidate,
      audio: audio?.candidate ?? null,
      muxerSupports,
    });
    if (combination === null) {
      triedVideo.push({ name: candidate.name, reason: 'codecs and container disagree' });
      continue;
    }

    videoSelection = video;
    audioSelection = audio;
    choice = combination;
    break;
  }

  if (videoSelection === null || choice === null) {
    return {
      ok: false,
      reason: 'no-codec',
      message: `No supported video codec at ${options.width}x${options.height}@${options.settings.fps}.`,
      tried: triedVideo.map((entry) => `${entry.name}: ${entry.reason}`),
    };
  }
  if (pcm !== null && audioSelection === null) {
    return {
      ok: false,
      reason: 'no-audio-codec',
      message:
        `No audio codec this browser can encode fits a ${choice.container.toUpperCase()} file ` +
        `at ${pcm.sampleRate} Hz / ${pcm.channelCount} channels.`,
      tried: triedAudio.map((entry) => `${entry.name}: ${entry.reason}`),
    };
  }

  const muxVideoCodec = muxerVideoCodec(videoSelection.candidate.codec);
  if (muxVideoCodec === null) {
    return {
      ok: false,
      reason: 'incompatible',
      message: `The muxer cannot describe ${videoSelection.candidate.name}.`,
    };
  }
  const muxAudioCodec =
    audioSelection === null ? null : muxerAudioCodec(audioSelection.candidate.codec);
  if (audioSelection !== null && muxAudioCodec === null) {
    return {
      ok: false,
      reason: 'incompatible',
      message: `The muxer cannot describe ${audioSelection.candidate.name}.`,
    };
  }

  // In-container packaging, not the raw stream Phase 7A preferred. An MP4 fed
  // Annex B produces a track that reads back as garbage.
  const videoConfig: VideoEncodeConfig = { ...videoSelection.config, bitstreamFormat: 'avc' };

  const muxer = new MediabunnyMuxer({
    container: choice.container,
    videoCodec: muxVideoCodec,
    ...(muxAudioCodec === null ? {} : { audioCodec: muxAudioCodec }),
    width: options.width,
    height: options.height,
    framerate: options.settings.fps,
  });

  const source = new CanvasFrameSource({
    prototype: options.prototype,
    ...(options.captureMethod === undefined ? {} : { method: options.captureMethod }),
    frameDurationMicros: frameDurationMicros(options.settings.fps),
  });
  // Both encoders hand their chunks to the session, which passes them to the
  // muxer in one serialised chain. The session does not exist yet — it needs
  // these — so the callbacks reach it through a holder.
  const holder: { session: AvExportSession | null } = { session: null };
  const videoEncoder = new WebCodecsVideoEncoder({
    onChunk: (chunk) => {
      holder.session?.acceptVideoChunk(chunk);
    },
  });
  const videoSession = new VideoExportSession({
    session: options.session,
    source,
    encoder: videoEncoder,
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.yieldToHost === undefined ? {} : { yieldToHost: options.yieldToHost }),
  });

  const audioEncoder =
    audioSelection === null
      ? null
      : new WebCodecsAudioEncoder({
          onChunk: (chunk) => {
            holder.session?.acceptAudioChunk(chunk);
          },
        });

  const chunkFrames =
    options.settings.audioChunkFrames ??
    (audioSelection === null ? 1024 : defaultChunkFrames(audioSelection.candidate));

  const session = new AvExportSession({
    video: videoSession,
    ...(pcm === null || audioEncoder === null || audioSelection === null
      ? {}
      : {
          audio: {
            pcm,
            source: new AudioDataChunkSource({ pcm, maxChunkFrames: chunkFrames }),
            encoder: audioEncoder,
            config: audioSelection.config,
          },
        }),
    muxer,
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.yieldToHost === undefined ? {} : { yieldToHost: options.yieldToHost }),
  });
  holder.session = session;

  const settings: AvExportSettings = { ...options.settings, audioChunkFrames: chunkFrames };

  return {
    ok: true,
    session,
    source,
    videoEncoder,
    audioEncoder,
    muxer,
    choice,
    videoConfig,
    videoSelection,
    audioSelection,
    settings,
  };
}

/** Why this buffer cannot be exported, or null when it can. */
function describePcmProblem(buffer: AudioBuffer): string | null {
  if (!Number.isFinite(buffer.sampleRate) || buffer.sampleRate <= 0) {
    return `the decoded audio reports an impossible sample rate (${buffer.sampleRate}).`;
  }
  if (buffer.numberOfChannels <= 0) return 'the decoded audio has no channels.';
  if (buffer.length <= 0) return 'the decoded audio contains no samples.';
  return null;
}
